import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from './useAuth';

export interface VideoProcessingSettingsResponse {
  stored: {
    width: number | null;
    height: number | null;
    frameRate: number | null;
    introVideoPath: string | null;
    outroVideoPath: string | null;
    masterAudioPath: string | null;
    overlayLogoPath: string | null;
    separatorImagePath: string | null;
    separatorVideoPath: string | null;
    separatorMaxVideoSeconds: number | null;
    updatedAt: string | null;
  };
  effective: {
    width: number;
    height: number;
    frameRate: number;
    introVideoPath: string | null;
    outroVideoPath: string | null;
    masterAudioPath: string | null;
    overlayLogoPath: string | null;
    separatorImagePath: string | null;
    separatorVideoPath: string | null;
    separatorMaxVideoSeconds: number;
  };
  resolvedFiles: {
    introExists: boolean;
    introAbsolute: string | null;
    outroExists: boolean;
    outroAbsolute: string | null;
    masterAudioExists: boolean;
    masterAudioAbsolute: string | null;
    overlayLogoExists: boolean;
    overlayLogoAbsolute: string | null;
    separatorImageExists: boolean;
    separatorVideoExists: boolean;
  };
  configFallback: {
    videoOutput: {
      width: number;
      height: number;
      frameRate: number;
      introVideoPath: string | null;
      outroVideoPath: string | null;
      masterAudioPath: string | null;
      overlayLogoPath: string | null;
    };
    separator: {
      imagePath: string | null;
      videoPath: string | null;
      maxVideoSeconds: number;
    };
  };
  /** Absolute directory where admin uploads are stored on the server (API hint only). */
  uploadDirectoryHint?: string;
}

export type VideoProcessingUploadSlot =
  | 'intro-video'
  | 'outro-video'
  | 'master-audio'
  | 'overlay-logo'
  | 'separator-image'
  | 'separator-video';

export type VideoProcessingSettingsPutPayload = Record<string, unknown>;

export const useVideoProcessingSettings = (enabled: boolean) =>
  useQuery<VideoProcessingSettingsResponse>(
    ['videoProcessingSettings'],
    async () => {
      const response = await api.get('/admin/video-processing-settings');
      return response.data;
    },
    {
      enabled,
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
    }
  );

export const useUpdateVideoProcessingSettings = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (payload: VideoProcessingSettingsPutPayload) => {
      const response = await api.put('/admin/video-processing-settings', payload);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['videoProcessingSettings']);
      },
    }
  );
};

export interface VideoProcessingUploadResponse {
  message: string;
  settings: VideoProcessingSettingsResponse;
}

export const useUploadVideoProcessingAsset = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ slot, file }: { slot: VideoProcessingUploadSlot; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post<VideoProcessingUploadResponse>(
        `/admin/video-processing-settings/upload/${slot}`,
        formData
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['videoProcessingSettings'], data.settings);
        queryClient.invalidateQueries(['videoProcessingSettings']);
      },
    }
  );
};
